import { create, StateCreator } from 'zustand';
import { AdvertEntity } from '../../domain/entities/Advert';
import { useCases } from '../../infrastructure/di/container';

interface AdvertsListState {
  items: AdvertEntity[];
  page: number;
  perPage: number;
  total: number;
  isLoading: boolean;
  current?: AdvertEntity;
  error?: string;
  searchQuery: string;
  isFetchingMore: boolean;
  list: (page?: number, perPage?: number, search?: string) => Promise<void>;
  get: (uuid: string) => Promise<void>;
  create: (title: string, description: string, files: { file: File; mimeType: string; }[]) => Promise<AdvertEntity | undefined>;
  update: (uuid: string, title: string, description: string, files: { file: File; mimeType: string; }[]) => Promise<AdvertEntity | undefined>;
  remove: (uuid: string) => Promise<void>;
  clearCurrent: () => void;
}

const creator: StateCreator<AdvertsListState> = (set, get) => ({
  items: [],
  page: 1,
  perPage: 12,
  total: 0,
  isLoading: false,
  searchQuery: '',
  isFetchingMore: false,
  list: async (page: number = 1, perPage: number = 12, search?: string) => {
    const currentSearch = search ?? get().searchQuery;
    const prevSearch = get().searchQuery;
    const isNextPage = page > 1 && currentSearch === prevSearch;
    if ((get().isLoading && !isNextPage) || get().isFetchingMore) return;
    set({ error: undefined, searchQuery: currentSearch, isLoading: !isNextPage, isFetchingMore: isNextPage });
    try {
      const res = await useCases.listAdverts().execute({ page, perPage, search: currentSearch });
      set((state) => ({
        items: isNextPage ? [...state.items, ...res.items] : res.items,
        page: res.page,
        perPage: res.perPage,
        total: res.total,
        isLoading: false,
        isFetchingMore: false,
      }));
    } catch (e: any) {
      set({ isLoading: false, isFetchingMore: false, error: e?.message || 'Failed to load adverts' });
    }
  },
  get: async (uuid: string) => {
    set({ isLoading: true, error: undefined });
    try {
      const entity = await useCases.getAdvert().execute(uuid);
      set({ current: entity, isLoading: false });
    } catch (e: any) {
      set({ isLoading: false, error: e?.message || 'Failed to get advert' });
    }
  },
  create: async (title: string, description: string, files: { file: File; mimeType: string; }[]) => {
    set({ isLoading: true, error: undefined });
    try {
      const entity = await useCases.createAdvert().execute({ title, description, attachments: files });
      set({ items: [entity, ...get().items], isLoading: false });
      return entity;
    } catch (e: any) {
      set({ isLoading: false, error: e?.message || 'Failed to create advert' });
    }
  },
  update: async (uuid: string, title: string, description: string, files: { file: File; mimeType: string; }[]) => {
    set({ isLoading: true, error: undefined });
    try {
      const entity = await useCases.updateAdvert().execute({ uuid, title, description, attachments: files });
      set({ items: get().items.map((i: AdvertEntity) => i.uuid === uuid ? entity : i), current: entity, isLoading: false });
      return entity;
    } catch (e: any) {
      set({ isLoading: false, error: e?.message || 'Failed to update advert' });
    }
  },
  remove: async (uuid: string) => {
    const prevItems = get().items;
    const prevTotal = get().total;
    const nextItems = prevItems.filter((i: AdvertEntity) => i.uuid !== uuid);
    const removed = prevItems.length !== nextItems.length ? 1 : 0;
    const nextCurrent = get().current?.uuid === uuid ? undefined : get().current;
    set({ items: nextItems, total: Math.max(0, prevTotal - removed), error: undefined, current: nextCurrent });
    try {
      await useCases.deleteAdvert().execute(uuid);
    } catch (e: any) {
      set({ items: prevItems, total: prevTotal, error: e?.message || 'Failed to delete advert', current: get().current });
    }
  },
  clearCurrent: () => set({ current: undefined })
});

export const useAdvertsStore = create<AdvertsListState>(creator);
